<template>
  <div class="content reward-editor">
    <div class="container-fluid">
      <card>
        <template slot="header">
          <div class="row">
            <div class="col">
              <header-navigator :routes="headerTitleData.data"></header-navigator>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <div class="rewards-guide-link" v-if="showRewardGuideLink">
                Looking for help? <a :href="rewardsGuideLink" target="_blank">View our rewards guide</a><span class="hide-message-outer"> (or <span class="hide-message-inner" @click="hideRewardsGuideLink">hide message</span>)</span>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <div class="errors" v-if="errors.length">
                <div class="errors-title">Whoops!</div>
                <ul>
                  <li class="error" v-for="error in errors" v-bind:key="error">{{ error }}</li>
                </ul>
              </div>
            </div>
          </div>
        </template>
        <template>
          <form>
            <div class="row">
              <div class="col-md-9">
                <div class="row">
                  <div class="col-md-8">
                    <fg-input type="text"
                              label="Title"
                              placeholder="Title"
                              :maxLength=30
                              :isRequired=true
                              v-model="reward.title">
                    </fg-input>
                  </div>
                  <div class="col-md-4">
                    <switch-group label="Status"
                                  tooltip="Turn ON to make your reward live in the Plyo App."
                                  v-model="reward.live">
                    </switch-group>
                  </div>
                </div>

                <div class="row">
                  <div class="col">
                    <div class="form-group">
                      <fg-textarea
                        :rows=3
                        label="Description"
                        placeholder="Description"
                        :maxLength=150
                        :isRequired=true
                        v-model="reward.description">
                      </fg-textarea>
                    </div>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-4">
                    <fg-input 
                      type="number"
                      step="50"
                      label="Point Value"
                      tooltip="Users earn 50 points per hour of exercise, or 100 with a friend. Point values typically range from 100 to 700."
                      :isRequired=true
                      v-model="reward.point_value">
                    </fg-input>
                  </div>
                  <div class="col-md-4">
                    <fg-select 
                      label="Reward Type"
                      disabledValue="Please select one"
                      tooltip="This is how our users will redeem your rewards."
                      :options="rewardTypes"
                      v-model="reward.reward_type"
                      :isRequired=true>
                    </fg-select>
                  </div>
                  <div class="col-md-4">
                    <fg-input 
                      type="text"
                      label="Promo Code"
                      tooltip="Users will receive this code to redeem online or through a point-of-sale system."
                      placeholder="Code"
                      v-model="reward.promo_code"
                      :isRequired=true
                      v-if="showPromoCodeFGInput">
                    </fg-input>
                    <fg-select 
                      label="Shopify Promo Code"
                      disabledValue="Select a code"
                      tooltip="This is the code or list of codes you created within the Plyo Shopify App."
                      :options="shopifyPriceRules"
                      v-model="reward.shopify_price_rule_title"
                      :isRequired=true
                      v-if="showPromoCodeShopifySelector">
                    </fg-select>
                  </div>
                </div>

                <div class="row" v-if="showRewardLinkFGInput">
                  <div class="col-md-8">
                    <fg-input 
                      type="text"
                      label="Website URL"
                      tooltip="Users will be directed to this url when they redeem."
                      placeholder="URL"
                      v-model="reward.link_url"
                      :isRequired=true>
                    </fg-input>
                  </div>
                </div>

                <div class="row">
                  <div class="col">
                    <switch-group label="Add Limits"
                                  tooltip="Limit the number of times your reward can be redeemed. We recommend this for high-value rewards."
                                  v-model="reward.add_limits">
                    </switch-group>
                  </div>
                </div>

                <div class="row" v-if="reward.add_limits">
                  <div class="col-md-4">
                    <fg-input type="number"
                              label="Per-User Weekly Limit"
                              tooltip="Limit the number of times each user can redeem your reward in a given week."
                              v-model="reward.per_user_weekly_limit">
                    </fg-input>
                  </div>
                  <div class="col-md-4">
                    <fg-input type="number"
                              label="Daily Limit"
                              tooltip="Limit the total number of times your reward can be redeemed each DAY."
                              v-model="reward.daily_limit">
                    </fg-input>
                  </div>
                  <div class="col-md-4">
                    <fg-input type="number"
                              label="Weekly Limit"
                              tooltip="Limit the total number of times your reward can be redeemed each WEEK."
                              v-model="reward.weekly_limit">
                    </fg-input>
                  </div>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="container d-flex justify-content-center">
                <small-button :buttonText="save_text" :processing-click="attempting_save" v-on:onButtonClicked="saveChanges" />
                <discard-button :buttonText="discard_text"  v-on:onButtonClicked="discardChanges"/>
              </div>
            </div>
          </form>
        </template>
      </card>
    </div>
  </div>
</template>

<script>

  import Card from '@/components/UIComponents/Cards/Card.vue'
  import Switch from '@/components/UIComponents/Inputs/Switch.vue'
  import HeaderNavigator from '@/components/UIComponents/HeaderNavigator.vue'
  import SmallButton from '@/components/UIComponents/Buttons/SmallButton.vue'
  import DiscardButton from '@/components/UIComponents/Buttons/DiscardButton.vue'

  export default {
    components: {
      Card,
      'switch-group': Switch,
      HeaderNavigator,
      SmallButton,
      DiscardButton,
    },
    data () {
      return {
        reward: {
          title: '',
          live: true,
          description: '',
          point_value: 100,
          reward_type: 'In-Store',
          promo_code: '',
          shopify_price_rule_title: '',
          link_url: '',
          add_limits: false,
          per_user_weekly_limit: '',
          daily_limit: '',
          weekly_limit: '',
        },
        errors: [],
        save_text: 'SAVE',
        discard_text: 'DISCARD',
        attempting_save: false,
      }
    },
    methods: {
      async hideRewardsGuideLink () {
        var payload = {
          merchant_id: this.$store.getters.advertiser.id,
        }

        await this.$store.dispatch('uploadHideRewardGuideLink', payload);
        this.$store.dispatch('hideRewardGuideLink');
      },
      goToRewardManager(title, message) {
        this.$store.dispatch('setModalTitle', { title: title })
        this.$store.dispatch('setSVGType', { svg_type: 'checkmark' })
        this.$store.dispatch('setModalMessage', { message: message })
        this.$store.dispatch('setModalVisible', { visible: true })
        const self = this;
        setTimeout(function () { 
            self.$router.push('/rewards'); 
            self.$store.dispatch('setModalVisible', { visible: false });
          }, 2000)
      },
      async saveChanges () {
        this.checkForErrors();
        if (!this.errors.length) {
          var payload = {};
          payload.upload_payload = this.getRewardUploadPayload();
          if (this.$router.currentRoute.path === '/reward/edit') {
            payload.reward_payload = this.getRewardPayload();

            if (this.attempting_save) {
              return;
            }
            this.attempting_save = true;
            await this.$store.dispatch('uploadRewardUpdates', payload);
            this.attempting_save = false;

            this.goToRewardManager('Changes Saved!', 'redirecting...')
          } else {
            payload.reward_payload = {
              advertiser_id: this.$store.getters.advertiser.id,
              reward: this.getRewardPayload(),
            }

            if (this.attempting_save) {
              return;
            }
            this.attempting_save = true;
            await this.$store.dispatch('uploadNewReward', payload);
            this.attempting_save = false;

            this.goToRewardManager('New Reward Created!', 'redirecting...');
          }
        } else {
          this.$emit('scrollTop', 0);
        }
      },
      discardChanges () {
        this.$store.dispatch('setModalTitle', { title: 'Changes Discarded!' })
        this.$store.dispatch('setSVGType', { svg_type: 'trash' })
        this.$store.dispatch('setModalMessage', { message: 'redirecting...' })
        this.$store.dispatch('setModalVisible', { visible: true })
        const self = this;
        setTimeout(function () { 
            self.$router.push('/rewards'); 
            self.$store.dispatch('setModalVisible', { visible: false });
          }, 2000)
      },
      checkForErrors () {
        this.errors = [];
        if (this.reward.title.length === 0) {
          this.errors.push('Title is required')
        }
        if (this.reward.description.length === 0) {
          this.errors.push('Description is required')
        }
        if (this.reward.point_value < 100 || this.reward.point_value > 1000) {
          this.errors.push('Point values should be between 100 and 1000')
        }
        if (this.isPromoCodeRewardType && (this.reward.promo_code.length === 0 && this.reward.shopify_price_rule_title.length === 0)) {
          this.errors.push('A promo code is required')
        }

        if (this.reward.add_limits) {
          var push_limit_error = false;
          if (this.reward.per_user_weekly_limit <= 0 && this.reward.per_user_weekly_limit != '') {
            push_limit_error = true
          }

          if (this.reward.daily_limit <= 0 && this.reward.daily_limit != '') {
            push_limit_error = true
          }
          
          if (this.reward.weekly_limit <= 0 && this.reward.weekly_limit != '') {
            push_limit_error = true
          }

          if (push_limit_error) {
            this.errors.push('Limits must be greater than 0.')
          }
        }
      },
      getRewardPayload () {
        var payload = {
          title: this.reward.title,
          live: this.reward.live,
          description: this.reward.description,
          point_value: this.reward.point_value,
          reward_type: this.reward.reward_type,
          promo_code: this.reward.promo_code,
          link_url: this.reward.link_url,
          add_limits: this.reward.add_limits,
          shopify_price_rule_id: this.getShopifyPriceRuleId(),
        }

        var date_modified = new Date();
        const options = { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric' };
        var last_edited = date_modified.toLocaleDateString("en-US", options);
        payload.last_edited = last_edited.charAt(0) + last_edited.slice(1).toLowerCase();

        const limits = this.getRewardLimits();
        payload.per_user_weekly_limit = limits.per_user_weekly_limit;
        payload.daily_limit = limits.daily_limit;
        payload.weekly_limit = limits.weekly_limit;

        if (this.$router.currentRoute.path === '/reward/edit') {
          payload.id = this.$store.getters.reward_being_edited.id;
          payload.advertiser_id = this.$store.getters.advertiser.id;
        }

        return payload
      },
      getRewardUploadPayload () {
        var payload = {
          merchant_id: this.$store.getters.advertiser.id,
          status: this.reward.live,
          title: this.reward.title,
          description: this.reward.description,
          point_value: this.reward.point_value,
        }

        if (this.reward.reward_type === 'In-Store') {
          payload.redeem_through_promocode = false;
          payload.redeem_online = false;
        } else if (this.reward.reward_type === 'Promo Code (point of sale)') {
          payload.redeem_through_promocode = true;
          payload.promocode = this.reward.promo_code;
          payload.redeem_online = false;
        } else if (this.reward.reward_type === 'Promo Code (online)') {
          payload.redeem_through_promocode = true;
          if (this.$store.getters.advertiser.is_shopify_merchant) {
            payload.shopify_price_rule_id = this.getShopifyPriceRuleId();
          } else {
            payload.promocode = this.reward.promo_code;
          }
          payload.link_url = this.reward.link_url;
          payload.redeem_online = true;
        } else if (this.reward.reward_type === 'Website URL (no code)') {
          payload.redeem_through_promocode = false;
          payload.link_url = this.reward.link_url;
          payload.redeem_online = true;
        }

        const limits = this.getRewardLimits();
        payload.per_user_weekly_limit = limits.per_user_weekly_limit;
        payload.daily_limit = limits.daily_limit;
        payload.weekly_limit = limits.weekly_limit;

        if (this.$router.currentRoute.path === '/reward/edit') {
          payload.reward_id = this.$store.getters.reward_being_edited.id;
        }

        return payload;
      },
      getRewardLimits () {
        var limits = {};

        if (this.reward.add_limits) {
          if (this.reward.per_user_weekly_limit != '') {
            limits.per_user_weekly_limit = this.reward.per_user_weekly_limit;
          } else {
            limits.per_user_weekly_limit = 10000;
          }
          if (this.reward.weekly_limit != '') {
            limits.weekly_limit = this.reward.weekly_limit;
          } else {
            limits.weekly_limit = 10000;
          }
          if (this.reward.daily_limit != '') {
            limits.daily_limit = this.reward.daily_limit;
          } else {
            limits.daily_limit = 10000;
          }
        } else {
          limits.per_user_weekly_limit = 10000;
          limits.weekly_limit = 10000;
          limits.daily_limit = 10000;
        }

        return limits
      },
      getShopifyPriceRuleId () {
        const price_rules = this.$store.getters.advertiser.price_rules;
        for (var i = 0; i < price_rules.length; i++) {
          if (price_rules[i].title === this.reward.shopify_price_rule_title.replace(' (bulk list)', '')) {
            return price_rules[i].id;
          }
        }
        return 0;
      }
    },
    computed: {
      isEditingReward() {
        return this.$router.currentRoute.path === '/reward/edit';
      },
      headerTitleData() {
        var header_title
        if (this.$router.currentRoute.path === '/reward/edit') {
          header_title = 'Edit Reward'
        } else {
          header_title = 'Add Reward'
        }

        var headerTitleData = {
          data: [
            {title: 'Manage Rewards', route: '/rewards'},
            {title: header_title, route: this.$router.currentRoute.path},
          ]
        }

        return headerTitleData;
      },
      showRewardGuideLink() {
        return !this.$store.getters.advertiser.has_hid_reward_guide_link;
      },
      rewardsGuideLink () {
        if (this.$store.getters.advertiser.company_type === "Restaurant") {
          return 'https://s3-us-west-2.amazonaws.com/plyo-merchant-resources/Getting+Started+Guide+-+Restaurants.pdf';
        } else if (this.$store.getters.advertiser.company_type === "Online Retail Store") {
          return 'https://s3-us-west-2.amazonaws.com/plyo-merchant-resources/Getting+Started+Guide+-+Online+Retail.pdf';
        } else if (this.$store.getters.advertiser.company_type === "Physical Retail Store") {
          return 'https://plyo-merchant-resources.s3-us-west-2.amazonaws.com/Getting+Started+Guide+-+In-Store+Retail.pdf';
        }
        return "/support";
      },
      isPromoCodeRewardType() {
        return ((this.reward.reward_type === 'Promo Code (online)') || (this.reward.reward_type === 'Promo Code (point of sale)'));
      },
      showPromoCodeFGInput () {
        return (this.isPromoCodeRewardType && (!this.$store.getters.advertiser.is_shopify_merchant));
      },
      showPromoCodeShopifySelector () {
        return (this.isPromoCodeRewardType && (this.$store.getters.advertiser.is_shopify_merchant));
      },
      showRewardLinkFGInput() {
        return ((this.reward.reward_type === 'Promo Code (online)') || (this.reward.reward_type === 'Website URL (no code)'));
      },
      rewardTypes () {
        if (this.$store.getters.advertiser.is_shopify_merchant) {
          return ['Promo Code (online)']
        } else {
          return ['In-Store', 'Promo Code (point of sale)', 'Promo Code (online)', 'Website URL (no code)'];
        }
      },
      shopifyPriceRules () {
        return this.$store.getters.advertiser.price_rules.map(price_rule => {
          var return_value = price_rule.title
          if (price_rule.discount_code_amount > 1) {
            return_value = return_value + ' (bulk list)'
          }
          return return_value;
        });
      }
    },
    mounted: async function () {
      this.$nextTick(() => {
        this.$emit('scrollTop', 0);
      });
      if ((this.$router.currentRoute.path === '/reward/edit') || (this.$store.getters.hasEditingReward)) {
        if (!this.$store.getters.hasEditingReward) {
          this.$router.push('/rewards');
        } else {
          var reward_being_edited = this.$store.getters.reward_being_edited
          if (reward_being_edited.description === undefined) {
            await this.$store.dispatch('obtainReward', reward_being_edited.id)
          }
          this.reward.title = reward_being_edited.title
          this.reward.live = reward_being_edited.live
          this.reward.description = reward_being_edited.description
          this.reward.point_value = reward_being_edited.point_value
          this.reward.reward_type = reward_being_edited.reward_type
          this.reward.promo_code = reward_being_edited.promo_code
          this.reward.link_url = reward_being_edited.link_url

          if (reward_being_edited.per_user_weekly_limit < 10000) {
            this.reward.per_user_weekly_limit = reward_being_edited.per_user_weekly_limit;
            this.reward.add_limits = true;
          }

          if (reward_being_edited.daily_limit < 10000) {
            this.reward.daily_limit = reward_being_edited.daily_limit;
            this.reward.add_limits = true;
          }

          if (reward_being_edited.weekly_limit < 10000) {
            this.reward.weekly_limit = reward_being_edited.weekly_limit;
            this.reward.add_limits = true;
          }

          if (this.$store.getters.advertiser.is_shopify_merchant) {
            const price_rules = this.$store.getters.advertiser.price_rules;
            for (var i = 0; i < price_rules.length; i++) {
              if (price_rules[i].id === reward_being_edited.shopify_price_rule_id) {
                this.reward.shopify_price_rule_title = price_rules[i].title;
                if (price_rules[i].discount_code_amount > 1) {
                  this.reward.shopify_price_rule_title = this.reward.shopify_price_rule_title + ' (bulk list)';
                }
              }
            }
          }
        } 
      } else {
        this.reward.link_url = this.$store.getters.advertiser.website_url;
        if (this.$store.getters.advertiser.company_type === "Online Retail Store") {
          this.reward.reward_type = 'Promo Code (online)';
        }
      }
    }
  }
</script>